import {
    v2,
    IVaccination,
    VaccinationCategory,
    VaccinationType,
    ConsentStatus,
    VaccineManufacturer,
    friendlyManufacturers,
    Flags,
} from '../../..';
import { PreviousDose } from '../../types';

type VaccinationParts = Pick<
    IVaccination,
    | 'hubId'
    | 'vaccinationCategory'
    | 'vaccinationType'
    | 'consented'
    | 'vaccineManufacturer'
    | 'itineraryId'
    | 'coAdministeredWith'
    | 'vaccineBatchNumber'
    | 'vaccineExpiryDate'
    | 'vaccineSiteOfAdministration'
    | 'flags'
    | 'doseNumber'
    | 'dateOfPreviousDose'
    | 'manufacturerOfPreviousDose'
    | 'dateOfDose1'
    | 'dateOfDose2'
    | 'dateOfDose3'
    | 'dateOfDose3Primary'
    | 'dateOfDose4'
    | 'manufacturerOfDose1'
    | 'manufacturerOfDose2'
    | 'manufacturerOfDose3'
    | 'manufacturerOfDose3Primary'
    | 'manufacturerOfDose4'
    | 'odsCode'
    | 'practice'
>;

const getManufacturer = (manufacturer?: string | null): string | undefined => {
    return Object.entries(friendlyManufacturers).find(([, value]) => manufacturer === value)?.[0];
};

const getPreviousDose = (
    doseNumber: number,
    vaccinationType: VaccinationType,
    previousDoses?: PreviousDose[] | null,
): (string | undefined)[] => {
    const previousDose = previousDoses?.find(
        (dose) => dose.doseNumber === doseNumber && dose.vaccinationType === vaccinationType,
    );
    const manufacturer = getManufacturer(previousDose?.manufacturer);
    const date = previousDose?.date?.toISOString();
    return [date, manufacturer];
};

export const vaccinationAdapter = (job: v2.Job): VaccinationParts => {
    const { patient, vaccination } = job;

    const { gpPracticeCode, gpPracticeName } = patient || {};

    const {
        hubId,
        vaccinationManufacturerId,
        vaccinationCategoryId,
        vaccinationType,
        consented,
        coAdminGroup,
        itineraryId,
        batchNumber,
        expiryDate,
        siteOfAdministration,
        previousDoses,
        doseNumber,
        vaccinationFlags,
    } = vaccination as v2.Vaccination;

    const coAdministeredWith =
        coAdminGroup?.vaccinations?.map((group) => group.vaccinationCategoryId) || [];

    const flags =
        vaccinationFlags?.map(
            (flag) =>
                Object.entries(Flags).find(([key]) => key === flag.flagId)?.[1] || flag.flagId,
        ) || [];

    const [dateOfDose1, manufacturerOfDose1] = getPreviousDose(
        1,
        VaccinationType.PRIMARY,
        previousDoses,
    );
    const [dateOfDose2, manufacturerOfDose2] = getPreviousDose(
        2,
        VaccinationType.PRIMARY,
        previousDoses,
    );
    const [dateOfDose3Primary, manufacturerOfDose3Primary] = getPreviousDose(
        3,
        VaccinationType.PRIMARY,
        previousDoses,
    );
    const [dateOfDose3, manufacturerOfDose3] = getPreviousDose(
        3,
        VaccinationType.BOOSTER,
        previousDoses,
    );
    const [dateOfDose4, manufacturerOfDose4] = getPreviousDose(
        4,
        VaccinationType.BOOSTER,
        previousDoses,
    );

    const previousDose = previousDoses?.[0];

    return {
        hubId: hubId || '',
        vaccinationCategory: vaccinationCategoryId as VaccinationCategory,
        vaccinationType: vaccinationType as VaccinationType,
        consented: consented as ConsentStatus,
        vaccineManufacturer: vaccinationManufacturerId as VaccineManufacturer,
        itineraryId: itineraryId || undefined,
        coAdministeredWith: coAdministeredWith as VaccinationCategory[],
        vaccineBatchNumber: batchNumber || undefined,
        vaccineExpiryDate: expiryDate ? expiryDate.toISOString() : undefined,
        vaccineSiteOfAdministration: siteOfAdministration || undefined,
        flags: flags as Flags[],
        doseNumber: doseNumber || null,
        dateOfPreviousDose: previousDose?.date?.toISOString(),
        manufacturerOfPreviousDose: getManufacturer(
            previousDose?.manufacturer,
        ) as VaccineManufacturer,
        dateOfDose1,
        dateOfDose2,
        dateOfDose3,
        dateOfDose3Primary,
        dateOfDose4,
        manufacturerOfDose1: manufacturerOfDose1 as VaccineManufacturer,
        manufacturerOfDose2: manufacturerOfDose2 as VaccineManufacturer,
        manufacturerOfDose3: manufacturerOfDose3 as VaccineManufacturer,
        manufacturerOfDose3Primary: manufacturerOfDose3Primary as VaccineManufacturer,
        manufacturerOfDose4: manufacturerOfDose4 as VaccineManufacturer,
        odsCode: gpPracticeCode || undefined,
        practice: gpPracticeName || undefined,
    };
};
